import { useSyncExternalStore } from "react";

// Selection and per-cell notes for the bulk tag grid, held outside React state on purpose.
// The grid is players × days — easily 1,500 cells — and keeping this in useState made every
// checkbox toggle and every keystroke rebuild the columns and re-render every cell. Here a cell
// subscribes to its own key, so a change re-renders that cell and the row/column summaries only.
export const createBulkSelection = () => {
    let selected = new Set();
    let notes = {};
    let version = 0;
    // What the grid was loaded with (see reset). Dirty = the live state differs from this.
    let baseline = { selected: new Set(), notes: {} };

    const cellListeners = new Map(); // key -> Set<fn>
    const cellSubscribers = new Map(); // key -> stable subscribe fn (useSyncExternalStore compares identity)
    const summaryListeners = new Set();

    const notifyKey = (key) => cellListeners.get(key)?.forEach((fn) => fn());
    const notifySummary = () => {
        version += 1;
        summaryListeners.forEach((fn) => fn());
    };

    const subscribeCell = (key) => {
        if (!cellSubscribers.has(key)) {
            cellSubscribers.set(key, (fn) => {
                if (!cellListeners.has(key)) cellListeners.set(key, new Set());
                cellListeners.get(key).add(fn);
                return () => cellListeners.get(key)?.delete(fn);
            });
        }
        return cellSubscribers.get(key);
    };

    const subscribeSummary = (fn) => {
        summaryListeners.add(fn);
        return () => summaryListeners.delete(fn);
    };

    return {
        isSelected: (key) => selected.has(key),
        noteOf: (key) => notes[key] || "",
        version: () => version,
        // Live references — read at save time; never mutate from outside.
        snapshot: () => ({ selected, notes }),
        // What reset() loaded: the cells the flag already carried and their notes. Saving
        // diffs against this to work out what to write and what to remove.
        baseline: () => baseline,
        subscribeCell,
        subscribeSummary,

        setKeys(keys, on) {
            const changed = [];
            keys.forEach((key) => {
                if (on && !selected.has(key)) {
                    selected.add(key);
                    changed.push(key);
                } else if (!on && selected.has(key)) {
                    selected.delete(key);
                    changed.push(key);
                }
            });
            if (changed.length === 0) return;
            changed.forEach(notifyKey);
            notifySummary();
        },

        setNote(key, note) {
            const next = note || "";
            if ((notes[key] || "") === next) return;
            if (next) notes[key] = next;
            else delete notes[key];
            notifyKey(key);
            notifySummary();
        },

        // Replace everything (open, close, or a new flag picked). Notifies every key that had or
        // now has state, so stale cells clear and prefilled ones appear.
        reset({ selected: keys = [], notes: nextNotes = {} } = {}) {
            const touched = new Set([...selected, ...keys, ...Object.keys(notes), ...Object.keys(nextNotes)]);
            selected = new Set(keys);
            notes = { ...nextNotes };
            baseline = { selected: new Set(keys), notes: { ...nextNotes } };
            touched.forEach(notifyKey);
            notifySummary();
        },
    };
};

export const useCellSelected = (store, key) =>
    useSyncExternalStore(store.subscribeCell(key), () => store.isSelected(key));

export const useCellNote = (store, key) =>
    useSyncExternalStore(store.subscribeCell(key), () => store.noteOf(key));

export const keyOf = (playerId, date) => `${playerId}_${date}`;

// The state of a select-all checkbox over `keys`: all checked, or some (indeterminate).
export const selectionOf = (store, keys) => {
    let count = 0;
    keys.forEach((key) => {
        if (store.isSelected(key)) count += 1;
    });
    return { all: count > 0 && count === keys.length, some: count > 0 && count < keys.length };
};

// Re-render on any change; the caller then reads what it needs off the store.
export const useSelectionVersion = (store) => useSyncExternalStore(store.subscribeSummary, store.version);
